/**
 * =============================================================================
 * ASL Multi-Tenant Partner Shop - Universal API Client
 * White-Label Compliant, Edge-Ready Client for Cloudflare Pages Functions
 * =============================================================================
 */

import { SHOP_CATEGORIES } from './shop-taxonomy.js';

export class ApiError extends Error {
  constructor(message, status = 500, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Base HTTP request executor with automatic header injection and error handling.
 */
export async function apiFetch(endpoint, options = {}) {
  const { headers = {}, body, tenantId, tenantDomain, token, ...customOptions } = options;

  const method = (options.method || 'GET').toUpperCase();
  const requestHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...headers,
  };

  if (tenantId) {
    requestHeaders['X-Tenant-ID'] = tenantId;
  }
  if (tenantDomain) {
    requestHeaders['X-Tenant-Domain'] = tenantDomain;
  }
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Mutating requests to administrative endpoints require X-ASL-Admin-CSRF header
  if ((method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') && endpoint.startsWith('/api/admin')) {
    if (!requestHeaders['X-ASL-Admin-CSRF']) {
      requestHeaders['X-ASL-Admin-CSRF'] = '1';
    }
  }

  const config = {
    method,
    headers: requestHeaders,
    credentials: 'same-origin',
    ...customOptions,
  };

  if (body && typeof body === 'object') {
    config.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(endpoint, config);
  } catch (err) {
    throw new ApiError(`Network connection error: ${err.message}`, 0, err);
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorMsg = data?.error || data?.message || `API request failed with HTTP ${response.status}`;
    throw new ApiError(errorMsg, response.status, data);
  }

  return data;
}

// -----------------------------------------------------------------------------
// Storefront Public APIs
// -----------------------------------------------------------------------------

/**
 * Resolves active tenant profile by hostname, subdomain, or slug.
 */
export async function resolveTenant(identifier) {
  const params = new URLSearchParams();
  if (identifier) {
    params.set('domain', identifier);
    params.set('tenant', identifier);
  }
  return apiFetch(`/api/tenant/resolve?${params.toString()}`);
}

/**
 * Retrieves active category taxonomy tree with tenant-scoped product counts.
 */
export async function getCategories(tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  return apiFetch(`/api/categories?${params.toString()}`, { tenantId });
}

/**
 * Fetches paginated catalog products with filters.
 */
export async function getProducts({
  tenantId,
  category,
  subcategory,
  aslCategory,
  search,
  featured,
  manufacturer,
  sort = 'recommended',
  page = 1,
  limit = 24,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort: String(sort),
  });

  if (tenantId) params.set('tenant_id', tenantId);
  if (category) params.set('category', category);
  if (subcategory) params.set('subcategory', subcategory);
  if (aslCategory !== undefined && aslCategory !== null) params.set('aslCategory', String(aslCategory));
  if (search && search.trim()) params.set('search', search.trim());
  if (manufacturer && manufacturer.trim()) params.set('manufacturer', manufacturer.trim());
  if (featured !== undefined && featured !== null) params.set('featured', featured ? '1' : '0');

  return apiFetch(`/api/products?${params.toString()}`, { tenantId });
}

/**
 * Retrieves single product detail by ID, SKU, or slug.
 */
export async function getProduct(productId, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  return apiFetch(`/api/products/${encodeURIComponent(productId)}?${params.toString()}`, { tenantId });
}

/**
 * Retrieves contextual product RAG metadata (linked documents, norms, AI suggestions).
 * @param {string} sku
 * @param {string} [tenantId]
 * @returns {Promise<{ ok: boolean, sku: string, product_name: string, documents: Array, suggested_queries: Array<string>, applicable_norms: Array<string> }>}
 */
/**
 * a-053: Dokumentliste eines Produkts (Tab "Dateien").
 * @param {string} sku
 * @param {string} [tenantId]
 */
export async function getProductDocuments(sku, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenant_id', tenantId);
  const qs = params.toString();
  return apiFetch(`/api/products/${encodeURIComponent(sku)}/documents${qs ? `?${qs}` : ''}`, { tenantId });
}

export async function getProductContext(sku, tenantId) {
  const params = new URLSearchParams({ sku });
  if (tenantId) params.set('tenant_id', tenantId);
  return apiFetch(`/api/rag/product-context?${params.toString()}`, { tenantId });
}


/**
 * Submits a B2B quote inquiry.
 */
export async function submitInquiry(inquiryData) {
  return apiFetch('/api/inquiries', {
    method: 'POST',
    tenantId: inquiryData.tenantId,
    body: inquiryData,
  });
}

/**
 * Submits an online order / checkout.
 */
export async function submitOrder(orderData) {
  return apiFetch('/api/orders', {
    method: 'POST',
    tenantId: orderData.tenantId,
    body: orderData,
  });
}

/**
 * Bestätigt eine Stripe-Zahlung (a-047). Gibt { order_number, status, payment_status } zurück.
 */
export async function verifyOrderPayment(sessionId, tenantId) {
  const params = new URLSearchParams({ session_id: sessionId });
  return apiFetch(`/api/orders/verify?${params.toString()}`, { method: 'GET', tenantId });
}

// -----------------------------------------------------------------------------
// Control Plane & Session APIs
// -----------------------------------------------------------------------------

/**
 * Authenticates against /api/session to establish an HttpOnly __Host-asl_session cookie.
 * @param {'hub' | 'admin'} scope
 * @param {string} secret
 * @returns {Promise<{ ok: boolean, authenticated: boolean, role: string, expiresAt: string }>}
 */
export async function loginSession(scope, secret) {
  return apiFetch('/api/session', {
    method: 'POST',
    body: { scope, secret },
  });
}

/**
 * Retrieves the current session status from /api/session.
 * @returns {Promise<{ ok: boolean, authenticated: boolean, role?: string, tenantId?: string, expiresAt?: string }>}
 */
export async function getSession() {
  return apiFetch('/api/session', {
    method: 'GET',
  });
}

/**
 * Tenant-Admin-Login über /api/session (bestehender AuthFlow, functions/api/session).
 * Setzt das HttpOnly __Host-asl_session Cookie für scope 'tenant_admin'.
 * @param {string} login
 * @param {string} password
 */
export async function loginTenantAdminSession(login, password) {
  return apiFetch('/api/session', {
    method: 'POST',
    body: { scope: 'tenant_admin', login, password },
  });
}

/**
 * Clears the session on /api/session.
 * @returns {Promise<{ ok: boolean, authenticated: boolean }>}
 */
export async function logoutSession() {
  return apiFetch('/api/session', {
    method: 'DELETE',
  });
}

// In-memory token store for transient session use without localStorage persistence
let inMemoryAdminKey = null;

export function getAdminApiKey() {
  return inMemoryAdminKey || null;
}

export function setAdminApiKey(key) {
  if (key && typeof key === 'string' && key.trim()) {
    inMemoryAdminKey = key.trim();
  } else {
    inMemoryAdminKey = null;
  }
}

export function clearAdminApiKey() {
  inMemoryAdminKey = null;
}

export async function adminListTenants(apiKey) {
  return apiFetch('/api/admin/tenants', { token: apiKey });
}

export async function adminGetTenant(tenantId, apiKey) {
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, { token: apiKey });
}

export async function adminCreateTenant(tenantData, apiKey) {
  return apiFetch('/api/admin/tenants', {
    method: 'POST',
    token: apiKey,
    body: tenantData,
  });
}

export async function adminUpdateTenant(tenantId, tenantData, apiKey) {
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'PUT',
    token: apiKey,
    body: tenantData,
  });
}

export async function adminListInquiries({ tenantId, status, page = 1, limit = 20 } = {}, apiKey) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (tenantId) params.set('tenant_id', tenantId);
  if (status) params.set('status', status);
  return apiFetch(`/api/admin/inquiries?${params.toString()}`, { token: apiKey });
}

export async function adminUpdateInquiryStatus(inquiryId, status, apiKey) {
  return apiFetch(`/api/admin/inquiries/${encodeURIComponent(inquiryId)}`, {
    method: 'PUT',
    token: apiKey,
    body: { status },
  });
}

export async function adminImportCatalog(importPayload, apiKey) {
  return apiFetch('/api/admin/catalog/import', {
    method: 'POST',
    token: apiKey,
    body: importPayload,
  });
}

/**
 * Retrieves tenant-specific product display information (overrides/original/status) (Paket P).
 */
export async function getAdminProductDisplay(productId, tenantId) {
  if (!productId || !tenantId) throw new Error('productId and tenantId are required');
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/products/${encodeURIComponent(productId)}`);
}

/**
 * Updates tenant-specific product display overrides (Paket P).
 */
export async function updateAdminProductDisplay(productId, tenantId, payload = {}) {
  if (!productId || !tenantId) throw new Error('productId and tenantId are required');
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/products/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Consolidated Admin API Client with automatic token resolution
 */
export const adminApi = {
  // 1. Tenants Management
  async getTenants(apiKey = getAdminApiKey()) {
    return adminListTenants(apiKey);
  },

  async getTenant(tenantId, apiKey = getAdminApiKey()) {
    return adminGetTenant(tenantId, apiKey);
  },

  async createTenant(tenantData, apiKey = getAdminApiKey()) {
    return adminCreateTenant(tenantData, apiKey);
  },

  async updateTenant(tenantId, tenantData, apiKey = getAdminApiKey()) {
    return adminUpdateTenant(tenantId, tenantData, apiKey);
  },

  // 2. Inquiries Management
  async getInquiries({ tenantId, status, page = 1, limit = 50 } = {}, apiKey = getAdminApiKey()) {
    return adminListInquiries({ tenantId, status, page, limit }, apiKey);
  },

  async getInquiry(inquiryId, apiKey = getAdminApiKey()) {
    return apiFetch(`/api/admin/inquiries/${encodeURIComponent(inquiryId)}`, { token: apiKey });
  },

  async updateInquiryStatus(inquiryId, status, notes = undefined, apiKey = getAdminApiKey()) {
    const body = { status };
    if (notes !== undefined) body.notes = notes;
    return apiFetch(`/api/admin/inquiries/${encodeURIComponent(inquiryId)}`, {
      method: 'PUT',
      token: apiKey,
      body,
    });
  },

  // 3. Catalog Synchronization
  async triggerCatalogImport(importPayload, apiKey = getAdminApiKey()) {
    return adminImportCatalog(importPayload, apiKey);
  },
};
